import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ChatCircleText, At, ArrowsDownUp, HeartStraight, UserPlus } from '@phosphor-icons/react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useAuth } from '../contexts/AuthContext';

const ICONS = {
  mention: At,
  comment: ChatCircleText,
  status_change: ArrowsDownUp,
  assigned: UserPlus,
  reaction: HeartStraight,
};
const ICON_BG = {
  mention: 'bg-violet-50 text-violet-600',
  comment: 'bg-blue-50 text-blue-600',
  status_change: 'bg-amber-50 text-amber-600',
  assigned: 'bg-emerald-50 text-emerald-600',
  reaction: 'bg-rose-50 text-rose-600',
};

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return d.toLocaleDateString();
}

// Bell in the sidebar workspace header. Opens a popover with recent notifications.
// Polls unread count every 30s while the popover is closed.
export default function NotificationBell() {
  const { getOrgHeaders, API, currentOrg } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const headers = useCallback(() => ({ headers: getOrgHeaders(), withCredentials: true }), [getOrgHeaders]);

  const fetchCount = useCallback(async () => {
    if (!currentOrg) return;
    try { const { data } = await axios.get(`${API}/notifications/unread-count`, headers()); setUnread(data.count || 0); } catch {}
  }, [API, currentOrg, headers]);

  const fetchItems = useCallback(async () => {
    if (!currentOrg) return;
    setLoading(true);
    try { const { data } = await axios.get(`${API}/notifications?limit=30`, headers()); setItems(data || []); }
    catch {} finally { setLoading(false); }
  }, [API, currentOrg, headers]);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 30000);
    return () => clearInterval(t);
  }, [fetchCount]);

  const handleOpen = (o) => {
    setOpen(o);
    if (o) { fetchItems(); fetchCount(); }
  };

  // Show ALL recent notifications (read + unread). The bell badge still
  // surfaces just the unread count. Earlier we filtered to unread-only,
  // but that made the bell look "broken" once a user had read everything,
  // even though the activity history still mattered.
  const visibleItems = items;

  const markAllRead = async () => {
    try { await axios.post(`${API}/notifications/read-all`, {}, headers()); setUnread(0); setItems(prev => prev.map(i => ({ ...i, read: true }))); } catch {}
  };

  const handleClick = async (n) => {
    if (!n.read) {
      try { await axios.post(`${API}/notifications/${n.notification_id}/read`, {}, headers()); } catch {}
      setUnread(u => Math.max(0, u - 1));
      setItems(prev => prev.map(i => i.notification_id === n.notification_id ? { ...i, read: true } : i));
    }
    setOpen(false);
    // Prefer task deep-link; otherwise fall back to the generic `link`
    // attribute (e.g. project_added → /projects/:id). Strip any absolute
    // URL prefix so React Router stays in-app.
    if (n.task_id) {
      navigate(`/tasks/${n.task_id}`);
    } else if (n.link) {
      try {
        const u = new URL(n.link, window.location.origin);
        navigate(u.pathname + u.search + u.hash);
      } catch { navigate(n.link); }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notification-bell"
          className="relative w-7 h-7 flex items-center justify-center rounded-md hover:bg-[var(--sb-hover)] text-[var(--sb-text-muted)] hover:text-[var(--sb-text)] transition-colors">
          <Bell size={15} weight={unread > 0 ? 'fill' : 'regular'} />
          {unread > 0 && (
            <span data-testid="notification-unread-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[14px] h-[14px] px-[3px] rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center border border-[var(--sb-bg)]">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-[380px] p-0 max-h-[560px] flex flex-col" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-100">
          <h3 className="text-[13px] font-semibold text-zinc-800">Notifications</h3>
          <button type="button" onClick={markAllRead} disabled={!unread}
            data-testid="notification-mark-all"
            className="text-[11px] text-blue-600 hover:text-blue-700 font-medium disabled:text-zinc-300 disabled:cursor-not-allowed">
            Mark all read
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading && <p className="text-center text-[12px] text-zinc-400 py-8">Loading…</p>}
          {!loading && visibleItems.length === 0 && (
            <div className="text-center py-12 px-4" data-testid="notification-empty">
              <Bell size={28} className="mx-auto text-zinc-300 mb-2" />
              <p className="text-[12px] text-zinc-500">You're all caught up</p>
            </div>
          )}
          {!loading && visibleItems.map(n => {
            const Icon = ICONS[n.type] || Bell;
            const bg = ICON_BG[n.type] || 'bg-zinc-50 text-zinc-600';
            return (
              <button key={n.notification_id} type="button" onClick={() => handleClick(n)}
                data-testid={`notification-item-${n.notification_id}`}
                className={`w-full flex items-start gap-3 px-4 py-3 border-b border-zinc-100 text-left transition-colors ${n.read ? 'hover:bg-zinc-50' : 'bg-blue-50/40 hover:bg-blue-50'}`}>
                <div className="relative flex-shrink-0">
                  {n.actor?.avatar ? (
                    <Avatar className="h-8 w-8"><AvatarImage src={n.actor.avatar} /><AvatarFallback className="bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[10px] font-bold">{n.actor?.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback></Avatar>
                  ) : (
                    <Avatar className="h-8 w-8"><AvatarFallback className="bg-gradient-to-br from-blue-400 to-indigo-500 text-white text-[10px] font-bold">{n.actor?.name?.charAt(0)?.toUpperCase() || '?'}</AvatarFallback></Avatar>
                  )}
                  <span className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center border-2 border-white ${bg}`}>
                    <Icon size={9} weight="bold" />
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-[12px] leading-tight ${n.read ? 'text-zinc-600' : 'text-zinc-800 font-medium'} mb-0.5`}>{n.title}</p>
                  {n.body && <p className="text-[11px] text-zinc-500 truncate">{n.body}</p>}
                  <p className="text-[10px] text-zinc-400 mt-1">{timeAgo(n.created_at)}</p>
                </div>
                {!n.read && <span className="flex-shrink-0 w-2 h-2 rounded-full bg-blue-500 mt-1.5" />}
              </button>
            );
          })}
        </div>
        {visibleItems.length > 0 && (
          <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50/50">
            <button type="button" onClick={() => { setOpen(false); navigate('/my-tasks'); }}
              className="w-full text-[11px] text-zinc-500 hover:text-zinc-700 font-medium flex items-center justify-center gap-1">
              <Check size={10} /> View all tasks
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
